import React, { useState, useEffect, useMemo } from 'react';
import { FaTimes, FaEye, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { previewTemplate, PreviewPayload, Template } from '../services/TemplatesService';
import axiosInstance from '../../../config/axios';
import { useCompensationUnlock } from '../../employees/hooks/useCompensationUnlock';
import { absolutizePdfUrl } from '../../../utils/pdfUtils';
import { ConditionalsPanel } from '../../contracts/components/ConditionalsPanel';
import { derivePresenceFromSchema, getFallbackPresence, validateConditionals } from '../../contracts/utils/conditionals';

interface TemplatePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: Template | null;
}

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  email?: string;
}

export function TemplatePreviewModal({
  isOpen,
  onClose,
  template,
}: TemplatePreviewModalProps) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [variablesJson, setVariablesJson] = useState('{}');
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Form values for conditional fields
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  const { ensureUnlocked, showPinDialog, pinError, handlePinSubmit, cancelUnlock } = useCompensationUnlock();

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e0e3ef',
    borderRadius: 6,
    fontSize: 14,
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: 6,
    fontSize: 13,
    fontWeight: 500,
    color: '#666',
  };

  useEffect(() => {
    if (isOpen) {
      loadEmployees();
      setVariablesJson('{}');
      setMissingFields([]);
      setSelectedEmployeeId(null);
      setPdfUrl(null);
      setFormValues({});
    }
  }, [isOpen]);

  const loadEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const { data } = await axiosInstance.get('/api/employees/', {
        params: { page_size: 100 },
      });

      // Handle paginated response (similar to listTemplates)
      let employeesList: Employee[];
      if (Array.isArray(data)) {
        employeesList = data;
      } else if (data.results && Array.isArray(data.results)) {
        employeesList = data.results;
      } else if (data.employees && Array.isArray(data.employees)) {
        employeesList = data.employees;
      } else {
        employeesList = [];
      }

      setEmployees(employeesList);
    } catch (err) {
      console.error('Failed to load employees:', err);
      toast.error('Failed to load employees');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handlePreview = async () => {
    if (!template) {
      return;
    }

    // TODO: Re-enable PIN verification for production if needed
    // if (template.contains_compensation) {
    //   const unlocked = await ensureUnlocked();
    //   if (!unlocked) {
    //     return;
    //   }
    // }

    setPreviewing(true);
    setMissingFields([]);

    try {
      // Parse manual JSON overrides
      let manualVariables: Record<string, any> = {};
      try {
        manualVariables = JSON.parse(variablesJson);
      } catch {
        toast.error('Invalid JSON in variables field');
        setPreviewing(false);
        return;
      }

      // B.3: Build complete variables object with proper structure
      const isFixedTerm = formValues.is_fixed_term === true || formValues.is_fixed_term === 'true';
      const hasOtherEmployment = formValues.has_other_employment === true || formValues.has_other_employment === 'true';

      const variables = {
        ...(selectedEmployeeId ? { employee_id: selectedEmployeeId } : {}),  // ← Employee ID für Auto-Population
        ...manualVariables,

        // § 1 Befristung
        ...(isFixedTerm ? {
          is_fixed_term: 'true',
          contract_end_date: formValues.contract_end_date || undefined,
          ...(formValues.fixed_term_reason ? { fixed_term_reason: formValues.fixed_term_reason } : {})
        } : { is_fixed_term: 'false' }),

        // § 7 Weitere Beschäftigung
        ...(hasOtherEmployment ? {
          has_other_employment: 'true',
          other_employment_employer: formValues.other_employment_employer || '',
          other_employment_street: formValues.other_employment_street || '',
          other_employment_house_no: formValues.other_employment_house_no || '',
          other_employment_postal_code: formValues.other_employment_postal_code || '',
          other_employment_city: formValues.other_employment_city || '',
          other_employment_start_date: formValues.other_employment_start_date || '',
          other_employment_monthly_salary: formValues.other_employment_monthly_salary || ''
        } : { has_other_employment: 'false' })
      };


      const payload: PreviewPayload = {
        variables,
      };

      const response = await previewTemplate(template.id, payload);

      if (response.missing_required_fields && response.missing_required_fields.length > 0) {
        setMissingFields(response.missing_required_fields);
        toast.warning('Some required fields are missing. Preview may be incomplete.');
      }

      // 1) Base64 → Blob → Blob URL
      if (response.pdf_bytes_b64) {
        const bin = atob(response.pdf_bytes_b64);
        const buf = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
        const blobUrl = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }));
        setPdfUrl(blobUrl);
        toast.success('Preview generated successfully');
      } else if (response.pdf_url) {
        // 2) Absolute/relative URL → absolutize
        const url = absolutizePdfUrl(response.pdf_url);
        if (url) {
          setPdfUrl(url);
          toast.success('Preview generated successfully');
        } else {
          toast.error('Kein PDF-Link vom Server erhalten');
        }
      } else if (response.render_status === 'locked') {
        // 3) Locked state (PIN required)
        toast.error(response.message || 'Vorschau gesperrt (PIN erforderlich).');
      } else {
        console.error('No pdf_url or pdf_bytes_b64 in response:', response);
        toast.error('Preview fehlgeschlagen: keine PDF vom Server erhalten.');
      }
    } catch (err: any) {
      console.error('Failed to generate preview:', err);

      const traceId = err?.response?.headers?.['x-trace-id'];
      const errorData = err?.response?.data;

      console.error('Error response data:', errorData);

      const errorMsg = errorData?.message || errorData?.detail || errorData?.error || 'Unknown error';
      const errorDetails = errorData?.details;
      const exceptionType = errorData?.exception_type;
      const exceptionMessage = errorData?.exception_message;
      const traceback = errorData?.traceback;

      let fullErrorMsg = `Failed to generate preview: ${errorMsg}`;

      if (exceptionType && exceptionMessage) {
        console.error(`Exception Type: ${exceptionType}`);
        console.error(`Exception Message: ${exceptionMessage}`);
        fullErrorMsg += `\n\n${exceptionType}: ${exceptionMessage}`;
      }

      if (traceback) {
        console.error('Full Traceback:', traceback);
      }

      if (errorDetails) {
        const detailsStr = typeof errorDetails === 'object'
          ? JSON.stringify(errorDetails, null, 2)
          : errorDetails;
        console.error('Error details:', detailsStr);
        fullErrorMsg += `\n${detailsStr}`;
      }

      if (traceId) {
        fullErrorMsg += ` (Trace: ${traceId})`;
      }

      toast.error(fullErrorMsg);
    } finally {
      setPreviewing(false);
    }
  };

  // Derive conditional field presence from schema (with fallback)
  const conditionalPresence = useMemo(() => {
    if (!template?.schema) {
      console.log('[TemplatePreview] No schema → using fallback presence');
      return getFallbackPresence();
    }

    const schemaKeys = template.schema.all_keys ?? [
      ...(template.schema.required_fields?.map(f => f.key) || []),
      ...(template.schema.optional_fields?.map(f => f.key) || []),
    ];

    if (schemaKeys.length === 0) {
      console.log('[TemplatePreview] Empty schema → using fallback presence');
      return getFallbackPresence();
    }

    const presence = derivePresenceFromSchema(schemaKeys);
    console.log('[TemplatePreview] Conditional presence:', presence);
    return presence;
  }, [template]);

  // Validation: Check if required conditional fields are filled (presence-aware)
  const isFormValid = useMemo(() => {
    const errors = validateConditionals(formValues, conditionalPresence);
    return Object.keys(errors).length === 0;
  }, [formValues, conditionalPresence]);

  if (!isOpen || !template) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1199,
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 0.3s ease',
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
      />

      {/* Drawer */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '700px',
          backgroundColor: 'white',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
          zIndex: 1200,
          display: 'flex',
          flexDirection: 'column',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease',
        }}
      >
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #e0e3ef' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#0f2b5a' }}>
                Preview Template
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: 13, color: '#666' }}>
                {template.name}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {pdfUrl && (
                <button
                  onClick={() => setPdfUrl(null)}
                  title="Back to Form"
                  style={{
                    padding: '6px',
                    background: '#e3f2fd',
                    color: '#1976d2',
                    border: '1px solid #1976d2',
                    borderRadius: 10,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FaEye size={12} />
                </button>
              )}
              <button
                onClick={onClose}
                title="Close"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '6px',
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FaTimes size={14} />
              </button>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: pdfUrl ? 0 : 24, display: pdfUrl ? 'flex' : 'block', flexDirection: 'column' }}>
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              style={{
                width: '100%',
                flex: 1,
                border: 'none',
              }}
              title="PDF Preview"
            />
          ) : (
            <>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Select Employee (Optional)</label>
              {loadingEmployees ? (
                <div style={{ padding: '8px 12px', color: '#666', fontSize: 13 }}>
                  Loading employees...
                </div>
              ) : (
                <>
                  <select
                    value={selectedEmployeeId || ''}
                    onChange={(e) =>
                      setSelectedEmployeeId(e.target.value ? Number(e.target.value) : null)
                    }
                    style={inputStyle}
                  >
                    <option value="">No employee (manual variables only)</option>
                    {employees
                      .filter((emp) => emp && emp.first_name && emp.last_name)
                      .map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name} {emp.email ? `(${emp.email})` : ''}
                        </option>
                      ))}
                  </select>
                  {employees.length === 0 && (
                    <div style={{
                      marginTop: 8,
                      padding: '8px 12px',
                      backgroundColor: '#fff3e0',
                      borderRadius: 4,
                      fontSize: 12,
                      color: '#f57c00'
                    }}>
                      ⚠️ No employees found. Create employees first or use manual variables.
                    </div>
                  )}
                  {employees.length > 0 && employees.filter((emp) => emp && emp.first_name && emp.last_name).length === 0 && (
                    <div style={{
                      marginTop: 8,
                      padding: '8px 12px',
                      backgroundColor: '#fff3e0',
                      borderRadius: 4,
                      fontSize: 12,
                      color: '#f57c00'
                    }}>
                      ⚠️ Found {employees.length} employee(s) but they have no names. Please update employee records.
                    </div>
                  )}
                </>
              )}
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                If selected, employee data (name, address, position, etc.) will be automatically populated in the preview.
              </div>
            </div>

            {/* Conditionals Panel - Schema-driven with presence-aware validation */}
            <ConditionalsPanel
              values={{
                is_fixed_term: formValues.is_fixed_term || false,
                contract_end_date: formValues.contract_end_date,
                fixed_term_reason: formValues.fixed_term_reason,
                has_other_employment: formValues.has_other_employment || false,
                other_employment_employer: formValues.other_employment_employer,
                other_employment_street: formValues.other_employment_street,
                other_employment_house_no: formValues.other_employment_house_no,
                other_employment_postal_code: formValues.other_employment_postal_code,
                other_employment_city: formValues.other_employment_city,
                other_employment_start_date: formValues.other_employment_start_date,
                other_employment_monthly_salary: formValues.other_employment_monthly_salary,
              }}
              onChange={(patch) => setFormValues((prev) => ({ ...prev, ...patch }))}
              presence={conditionalPresence}
            />

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Variable Overrides (JSON)</label>
              <textarea
                value={variablesJson}
                onChange={(e) => setVariablesJson(e.target.value)}
                style={{
                  ...inputStyle,
                  minHeight: 150,
                  fontFamily: 'monospace',
                  fontSize: 13,
                }}
                placeholder='{"field_key": "custom value"}'
              />
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                Override specific variables or provide values when no employee is selected.
              </div>
            </div>

            {missingFields.length > 0 && (
              <div
                style={{
                  padding: 12,
                  backgroundColor: '#fff3e0',
                  border: '1px solid #f57c00',
                  borderRadius: 6,
                  marginBottom: 20,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 8,
                    color: '#f57c00',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  <FaExclamationTriangle size={14} />
                  Missing Required Fields
                </div>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#666' }}>
                  {missingFields.map((field) => (
                    <li key={field}>{field}</li>
                  ))}
                </ul>
              </div>
            )}

            {template.schema?.required_fields && template.schema.required_fields.length > 0 && (
              <div
                style={{
                  padding: 12,
                  backgroundColor: '#f8f9fc',
                  borderRadius: 6,
                  marginBottom: 20,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f2b5a', marginBottom: 8 }}>
                  Required Fields in Schema:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {template.schema.required_fields.map((field) => (
                    <span
                      key={field.key}
                      style={{
                        padding: '4px 8px',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                      }}
                    >
                      {field.label} ({field.key})
                    </span>
                  ))}
                </div>
              </div>
            )}
            </>
          )}
          {!pdfUrl && (
            <div
              style={{
                padding: 24,
                borderTop: '1px solid #e0e3ef',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 12,
                marginTop: 'auto',
              }}
            >
              <button
                onClick={onClose}
                disabled={previewing}
                style={{
                  padding: '10px 20px',
                  background: '#f5f5f5',
                  border: '1px solid #e0e3ef',
                  borderRadius: 10,
                  cursor: previewing ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#666',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handlePreview}
                disabled={previewing || !isFormValid}
                style={{
                  padding: '10px 20px',
                  background: (previewing || !isFormValid) ? '#ccc' : '#021a4a',
                  border: 'none',
                  borderRadius: 10,
                  cursor: (previewing || !isFormValid) ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <FaEye size={14} />
                {previewing ? 'Generating...' : 'Generate Preview'}
              </button>
            </div>
          )}
        </div>
      </div>

      {showPinDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={cancelUnlock}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: 12,
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: 24, borderBottom: '1px solid #e0e3ef' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0f2b5a' }}>
                Enter Compensation PIN
              </h3>
            </div>

            <div style={{ padding: 24 }}>
              <p style={{ marginBottom: 20, color: '#666', fontSize: 14 }}>
                Previewing a template with compensation data requires PIN verification.
              </p>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  const pin = formData.get('pin') as string;
                  await handlePinSubmit(pin);
                }}
              >
                <div>
                  <label style={labelStyle}>PIN</label>
                  <input
                    type="password"
                    name="pin"
                    autoFocus
                    style={{
                      ...inputStyle,
                      border: pinError ? '1px solid #F44336' : '1px solid #e0e3ef',
                    }}
                  />
                  {pinError && (
                    <div style={{ marginTop: 4, color: '#F44336', fontSize: 12 }}>
                      {pinError}
                    </div>
                  )}
                </div>

                <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                  <button
                    type="button"
                    onClick={cancelUnlock}
                    style={{
                      padding: '10px 20px',
                      background: '#f5f5f5',
                      border: '1px solid #e0e3ef',
                      borderRadius: 10,
                      cursor: 'pointer',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#666',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '10px 20px',
                      background: '#021a4a',
                      border: 'none',
                      borderRadius: 10,
                      cursor: 'pointer',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#fff',
                    }}
                  >
                    Unlock
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
